import i18n from 'i18next';
import React, { FC } from 'react';
import type {
  AdjusmentSource,
  AdjusmentStatus
} from '@melio/payments-recon-api-client-ts';
import { Tag } from '@tools/ui/components/common/Tag';
import {
  AllCasesSubStatus,
  EnumTaskEntityCachedDataPaymentRiskStatus,
  PaymentriskStatusEnumType,
  Status as ChargebackStatus,
  TransactionsourceEnumType
} from '@tools/ui/types/generated/graphql';
import { TranslationCommon } from '@tools/ui-locales';
import colors from '@tools/ui-utils/colors.util';
import { splitAndCapitalizeFirstWord } from '@tools/ui-utils/stringFormat.util';
import { t } from '@tools/ui-utils/translate.util';
import { reconEnumValue } from '../../utils/reconEnumValue';

type TProps = {
  status?:
    | PaymentriskStatusEnumType
    | EnumTaskEntityCachedDataPaymentRiskStatus
    | ChargebackStatus
    | AllCasesSubStatus
    | TransactionsourceEnumType
    | AdjusmentStatus
    | AdjusmentSource;
  humanize?: boolean;
};

const normalizeStatusKey = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '');

const statusColor = {
  // risk status
  [PaymentriskStatusEnumType.declined]: colors.error,
  [PaymentriskStatusEnumType.approved]: colors.green,
  [PaymentriskStatusEnumType.pending]: colors.geekblue,
  // reco status
  [TransactionsourceEnumType.svb]: colors.daybreak,
  [TransactionsourceEnumType.evolve]: colors.goldenPurple,
  // reco adjustment status
  [reconEnumValue<AdjusmentStatus>('created')]: colors.geekblue,
  [reconEnumValue<AdjusmentStatus>('alerted')]: colors.error,
  [reconEnumValue<AdjusmentStatus>('settled')]: colors.green,
  // reco adjustment source
  [reconEnumValue<AdjusmentSource>('manual')]: colors.daybreak,
  [reconEnumValue<AdjusmentSource>('automated')]: colors.goldenPurple,
  // Lucy status
  [normalizeStatusKey(ChargebackStatus.Lost)]: colors.error,
  [normalizeStatusKey(ChargebackStatus.Recovered)]: colors.green,
  [normalizeStatusKey(ChargebackStatus.PotentialLoss)]: colors.geekblue,
  [normalizeStatusKey(ChargebackStatus.PartnerLoss)]: colors.gray,
  [normalizeStatusKey(ChargebackStatus.ResolvedPotentialLoss)]: colors.gray,
  // Lucy sub-status
  [normalizeStatusKey(AllCasesSubStatus.Final)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.RecoverySuccessful)]: colors.green,
  [normalizeStatusKey(AllCasesSubStatus.AttemptingRecovery)]: colors.gold,
  [normalizeStatusKey(AllCasesSubStatus.RecoveryFailed)]: colors.error,
  [normalizeStatusKey(AllCasesSubStatus.FalsePositive)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.InquiryLost)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.InquiryResponded)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.InquiryWon)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.LossReviewed)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.MelioHasFunds)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.NotFighting)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.NotFinal)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.Other)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.PoaAnswered)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.RemovedByPayor)]: colors.gray,
  [normalizeStatusKey(AllCasesSubStatus.Tbd)]: colors.gray
};

// Lucy status/sub-status values have translated labels; every other status
// kind consumed by this component keeps falling back to the humanized raw
// value below.
const statusLabelKey: Record<string, TranslationCommon> = {
  [normalizeStatusKey(ChargebackStatus.Lost)]:
    'reco.transferSearch.caseStatus.lost',
  [normalizeStatusKey(ChargebackStatus.Recovered)]:
    'reco.transferSearch.caseStatus.recovered',
  [normalizeStatusKey(ChargebackStatus.PotentialLoss)]:
    'reco.transferSearch.caseStatus.potentialLoss',
  [normalizeStatusKey(ChargebackStatus.PartnerLoss)]:
    'reco.transferSearch.caseStatus.partnerLoss',
  [normalizeStatusKey(ChargebackStatus.ResolvedPotentialLoss)]:
    'reco.transferSearch.caseStatus.resolvedPotentialLoss',
  [normalizeStatusKey(AllCasesSubStatus.Final)]:
    'reco.transferSearch.caseSubStatus.final',
  [normalizeStatusKey(AllCasesSubStatus.RecoverySuccessful)]:
    'reco.transferSearch.caseSubStatus.recoverySuccessful',
  [normalizeStatusKey(AllCasesSubStatus.AttemptingRecovery)]:
    'reco.transferSearch.caseSubStatus.attemptingRecovery',
  [normalizeStatusKey(AllCasesSubStatus.RecoveryFailed)]:
    'reco.transferSearch.caseSubStatus.recoveryFailed',
  [normalizeStatusKey(AllCasesSubStatus.FalsePositive)]:
    'reco.transferSearch.caseSubStatus.falsePositive',
  [normalizeStatusKey(AllCasesSubStatus.InquiryLost)]:
    'reco.transferSearch.caseSubStatus.inquiryLost',
  [normalizeStatusKey(AllCasesSubStatus.InquiryResponded)]:
    'reco.transferSearch.caseSubStatus.inquiryResponded',
  [normalizeStatusKey(AllCasesSubStatus.InquiryWon)]:
    'reco.transferSearch.caseSubStatus.inquiryWon',
  [normalizeStatusKey(AllCasesSubStatus.LossReviewed)]:
    'reco.transferSearch.caseSubStatus.lossReviewed',
  [normalizeStatusKey(AllCasesSubStatus.MelioHasFunds)]:
    'reco.transferSearch.caseSubStatus.melioHasFunds',
  [normalizeStatusKey(AllCasesSubStatus.NotFighting)]:
    'reco.transferSearch.caseSubStatus.notFighting',
  [normalizeStatusKey(AllCasesSubStatus.NotFinal)]:
    'reco.transferSearch.caseSubStatus.notFinal',
  [normalizeStatusKey(AllCasesSubStatus.Other)]:
    'reco.transferSearch.caseSubStatus.other',
  [normalizeStatusKey(AllCasesSubStatus.PoaAnswered)]:
    'reco.transferSearch.caseSubStatus.poaAnswered',
  [normalizeStatusKey(AllCasesSubStatus.RemovedByPayor)]:
    'reco.transferSearch.caseSubStatus.removedByPayor',
  [normalizeStatusKey(AllCasesSubStatus.Tbd)]:
    'reco.transferSearch.caseSubStatus.tbd'
};

const getHumanizedLabel = (status: string): string => {
  const labelKey = statusLabelKey[normalizeStatusKey(status)];

  return labelKey && i18n.exists(labelKey)
    ? t(labelKey)
    : splitAndCapitalizeFirstWord(status);
};

const TagStatus: FC<React.PropsWithChildren<TProps>> = ({
  status,
  humanize
}) => {
  const color =
    (status && statusColor[normalizeStatusKey(status)]) || colors.geekblue;

  return (
    <Tag color={color} style={humanize ? { textTransform: 'none' } : undefined}>
      {humanize && status ? getHumanizedLabel(status) : status}
    </Tag>
  );
};

export default TagStatus;
